import { useCallback, useState } from 'react'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Loading from '@/elements/Loading'
import Table from '@/components/Table'
import Modal from '@/components/Modal'
import Pagination from '@/components/Pagination'
import useBreakpoints from '@/hooks/useBreakpoints'
import { createOrganizationMember, useGetOrganizationMembers } from '@/api/organizationMember'
import {
  useOrganizationMembersTable,
  type OrganizationMemberTableType
} from '@/hooks/tables/organizationMembersTable'
import usePagination from '@/hooks/usePagination'
import useFiltering from '@/hooks/useFiltering'
import useDeactivateUser from '@/hooks/useDeactivateUser'
import useOrganizationMemberForm from '@/hooks/forms/useOrganizationMemberForm'
import toast from '@/utils/toast'

import OrganizationMembersFilterBar from './OrganizationMembersFilterBar'
import OrganizationMemberForm from './OrganizationMemberForm'

function OrganizationMembers() {
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [memberToDeleteId, setMemberToDeleteId] = useState<string | null>(null)
  const { isTablet, isMobile } = useBreakpoints()
  const { form, resetForm } = useOrganizationMemberForm(null)
  const { page, limit, setPage } = usePagination(10)
  const { updateFilterBy, allFilters } = useFiltering<['name', 'roleName']>(() => setPage(1))
  const { data: organizationMembers, isLoading, refetch } = useGetOrganizationMembers({
    page,
    limit,
    filters: {
      'filter[name]': allFilters.name,
      ...(allFilters.roleName ? Object.fromEntries(
        allFilters.roleName.split(',')
          .map(r => r.trim()).filter(r => r)
          .map((roleName, index) => [`filter[roleName][${index}]`, roleName])
      ) : {}),
    },
  })
  const {
    setShowDeactivateModal,
    DeactivateUserModal
  } = useDeactivateUser({
    userType: 'member',
    userId: memberToDeleteId || '',
    refetch
  })
  const tableData = useOrganizationMembersTable(
    organizationMembers?.data?.items || [],
    (id: string) => {
      setMemberToDeleteId(id)
      setShowDeactivateModal(true)
    }
  )

  const submitNewMember = useCallback(async () => {
    const isValid = await form.trigger()
    const dirtyFields = form.formState.dirtyFields
    if (isValid) {
      const values = form.getValues()
      const formData = new FormData()
      Object.entries({
        ...(dirtyFields.avatar && { avatar: values.avatar || '' }),
        firstName: values.firstName,
        lastName: values.lastName || '',
        email: values.email,
        city: values.city || '',
        state: values.state || '',
        role: values.role || '',
      }).forEach(([key, value]) => formData.append(key, value))

      try {
        await createOrganizationMember(formData)
        setShowAddUserModal(false)
        resetForm(null)
        toast.success('Member added successfully!')
      } catch {
        toast.error('You are attempting to add a user that has already been set-up in the system. If you require further assistance please reach out to your Fuzzy Watchdog Support Team.')
      }
    }
  }, [form, resetForm])

  return (
    <Flex className='card' wfull gap={7} p={isMobile ? 3 : isTablet ? 5 : 7}>
      <Flex gap={2}>
        <Text variant='h3'>Members</Text>
        <Text color='black-light-40'>
          Manage members of your organization and their roles.
        </Text>
      </Flex>
      <Flex wfull gap={4}>
        <OrganizationMembersFilterBar
          filterByName={allFilters.name || ''}
          setFilterByName={q => updateFilterBy('name', q)}
          filterByRole={allFilters.roleName || ''}
          setFilterByRole={r => updateFilterBy('roleName', r)}
          refetchMembers={refetch}
          handleOpenAddUserModal={() => setShowAddUserModal(true)}
        />
        <Modal
          title='Add Member'
          isOpen={showAddUserModal}
          onCancel={() => {
            setShowAddUserModal(false)
            form.reset()
          }}
          onSave={submitNewMember}
          saveLabel='Send Invitation'
          disabled={!form.formState.isDirty}
          style={{ width: 538 }}
          preventClickOutsideClose={form.formState.isDirty}
        >
          <OrganizationMemberForm form={form} mode='add' onSubmit={submitNewMember} />
        </Modal>

        {isLoading ? (
          <Flex wfull align='center' justify='center' py={8}>
            <Loading />
          </Flex>
        ) : (
          <>
            <Table<OrganizationMemberTableType> {...tableData} />
            <DeactivateUserModal />
          </>
        )}
        <Flex wfull align='center' pt={4} mt='auto'>
          <Pagination
            variant={isMobile ? 'compact' : 'default'}
            onPageChange={setPage}
            {...organizationMembers?.data?.pagination}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}

export default OrganizationMembers
